import { useId, useMemo } from 'react';
import { Info, Loader2 } from 'lucide-react';
import TooltipSimple from '../ui/TooltipSimple';
import type { ExcursionAvailability } from '../../lib/trades/tradeDetailsDeterministicAdapter';

export type TradeExcursionWidgetProps = {
  /** Realized P&L for the currently selected Gross/Net page mode. */
  closedPnl: number;
  /** Maximum adverse excursion for the selected mode, normally negative. */
  mae: number | null;
  /** Maximum favorable excursion for the selected mode. */
  mfe: number | null;
  title?: string;
  className?: string;
  currency?: string;
  pnlMode?: 'GROSS' | 'NET';
  availability?: ExcursionAvailability;
  unavailableReason?: string | null;
  isUpdating?: boolean;
  isOpen?: boolean;
};

type Point = { x: number; y: number };

export type TradeExcursionWidgetData = {
  heatSurvived: number;
  capturedProfit: number;
  leftOnTable: number;
  heatPct: number;
  capturedPct: number;
  leftPct: number;
  efficiency: number | null;
  outcome: 'profit' | 'loss' | 'flat';
  gaugeSegments: [number, number, number];
  maxDrawdown: number | null;
  recoveredBeforeExit: number | null;
  drawdownRecoveredPercent: number | null;
  drawdownRemainingPercent: number | null;
  missedUpside: number | null;
};

const ARC_RADIUS = 120;
const ARC_PATH = 'M 30 150 A 120 120 0 0 1 270 150';
const INSET_PATH = 'M 30 150 A 120 120 0 0 1 270 150 L 270 150 L 30 150 Z';
const LOSS_COLOR = '#EF4444';
const PROFIT_COLOR = '#22C55E';
const LEFT_ON_TABLE_ACCENT = '#5b6e83';

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const pointOnArc = (percent: number): Point => {
  const normalized = clamp(percent, 0, 100) / 100;
  const angle = Math.PI - Math.PI * normalized;

  return {
    x: 150 + ARC_RADIUS * Math.cos(angle),
    y: 150 - ARC_RADIUS * Math.sin(angle),
  };
};

export const calculateTradeExcursionWidgetData = (
  closedPnl: number,
  mae: number | null,
  mfe: number | null,
): TradeExcursionWidgetData => {
  const hasMae = mae != null && Number.isFinite(mae);
  const hasMfe = mfe != null && Number.isFinite(mfe);
  const maeValue = hasMae ? Number(mae) : null;
  const mfeValue = hasMfe ? Number(mfe) : null;
  const heatSurvived = Math.abs(Math.min(maeValue ?? 0, 0));
  const capturedProfit = Math.max(Number.isFinite(closedPnl) ? closedPnl : 0, 0);
  const favorableExcursion = Math.max(mfeValue ?? 0, 0);
  const leftOnTable = Math.max(favorableExcursion - capturedProfit, 0);
  const outcome = closedPnl > 0 ? 'profit' : closedPnl < 0 ? 'loss' : 'flat';
  const profitCaptureEfficiency = favorableExcursion > 0
    ? clamp((capturedProfit / favorableExcursion) * 100, 0, 100)
    : 0;
  // A profitable trade's gauge uses MFE as its only denominator. MAE remains
  // useful context in the bottom card, but it is not part of capture efficiency.
  const heatPct = 0;
  const capturedPct = outcome === 'loss' ? 0 : profitCaptureEfficiency;
  const leftPct = outcome === 'loss' ? 0 : Math.max(0, 100 - capturedPct);
  const realizedLoss = Math.abs(Math.min(Number.isFinite(closedPnl) ? closedPnl : 0, 0));
  const maxDrawdown = outcome === 'loss' && maeValue != null && Math.abs(maeValue) > 0
    ? Math.abs(maeValue)
    : null;
  const recoveredBeforeExit = maxDrawdown != null
    ? Math.max(maxDrawdown - realizedLoss, 0)
    : null;
  const drawdownRecoveredPercent = maxDrawdown != null && recoveredBeforeExit != null
    ? clamp((recoveredBeforeExit / maxDrawdown) * 100, 0, 100)
    : null;
  const drawdownRemainingPercent = drawdownRecoveredPercent != null
    ? 100 - drawdownRecoveredPercent
    : null;
  const missedUpside = outcome === 'loss' && mfeValue != null && mfeValue > 0 ? mfeValue : null;
  const lossSegments: [number, number, number] = drawdownRecoveredPercent != null
    ? [drawdownRemainingPercent ?? 0, drawdownRecoveredPercent, 0]
    : [0, 0, 100];

  return {
    heatSurvived,
    capturedProfit,
    leftOnTable,
    heatPct,
    capturedPct,
    leftPct,
    efficiency: outcome === 'loss' ? drawdownRecoveredPercent : profitCaptureEfficiency,
    outcome,
    gaugeSegments: outcome === 'loss'
      ? lossSegments
      : [0, capturedPct, leftPct],
    maxDrawdown,
    recoveredBeforeExit,
    drawdownRecoveredPercent,
    drawdownRemainingPercent,
    missedUpside,
  };
};

const MetricCard = ({
  label,
  value,
  accent,
  valueColor,
}: {
  label: string;
  value: string;
  accent: string;
  valueColor: string;
}) => (
  <div
    className="trade-excursion-summary-card min-w-0 [container-type:inline-size] rounded-lg border border-[#242428] border-t-2 bg-[#1d1d20] px-2 py-2.5 text-center shadow-[inset_0_3px_8px_rgba(0,0,0,0.10)]"
    style={{ borderTopColor: accent }}
  >
    <div className="trade-excursion-card-label mb-2 font-normal leading-none text-[#aeb8d1]">
      {label}
    </div>
    <div
      className="trade-excursion-card-value font-mono font-semibold leading-none tracking-[-0.025em]"
      style={{ color: valueColor }}
    >
      {value}
    </div>
  </div>
);

/**
 * Profit Capture Efficiency = Captured Profit / Maximum Favorable Excursion.
 *
 * The values passed to this component already represent the page's selected
 * Gross/Net mode. Fees must not be applied again here.
 */
export default function TradeExcursionWidget({
  closedPnl,
  mae,
  mfe,
  title = 'Trade Excursion',
  className = '',
  currency = 'USD',
  availability = 'available',
  unavailableReason = null,
  isUpdating = false,
  isOpen = false,
}: TradeExcursionWidgetProps) {
  const rawId = useId();
  const id = rawId.replace(/:/g, '');
  const data = useMemo(
    () => calculateTradeExcursionWidgetData(closedPnl, mae, mfe),
    [closedPnl, mae, mfe],
  );

  const money = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [currency],
  );

  const [firstPct, secondPct, thirdPct] = data.gaugeSegments;
  const firstEnd = firstPct;
  const secondEnd = firstPct + secondPct;
  const marker = pointOnArc(data.outcome === 'loss' ? firstEnd : secondEnd);
  const firstStartPoint = pointOnArc(0);
  const firstEndPoint = pointOnArc(firstEnd);
  const secondStartPoint = pointOnArc(firstEnd);
  const secondEndPoint = pointOnArc(secondEnd);
  const thirdStartPoint = pointOnArc(secondEnd);
  const thirdEndPoint = pointOnArc(100);
  const isLoss = data.outcome === 'loss';
  const mainMetricGradient = data.efficiency == null || data.efficiency === 0
    ? 'linear-gradient(90deg, #a1a1aa 0%, #d4d4d8 100%)'
    : isLoss
    ? 'linear-gradient(90deg, #f87171 0%, #fecaca 58%, #ffffff 100%)'
    : 'linear-gradient(90deg, #45d9ac 0%, #55dfb5 54%, #f4f4f5 70%, #ffffff 100%)';
  const gaugeLabel = isLoss ? 'DRAWDOWN RECOVERED' : 'CAPTURE EFFICIENCY';

  const formatSigned = (value: number | null, sign: '+' | '-') =>
    value == null ? '—' : `${sign}${money.format(Math.abs(value))}`;
  const gaugeAriaLabel = isLoss
    ? data.drawdownRecoveredPercent == null
      ? 'Drawdown recovered unavailable because maximum adverse excursion is missing.'
      : `${data.drawdownRecoveredPercent.toFixed(2)}% of the maximum drawdown was recovered before exit.`
    : `${Math.round(data.efficiency ?? 0)} percent capture efficiency`;
  const hasCompleteExcursion = availability === 'available' && mae != null && mfe != null && Number.isFinite(Number(mae)) && Number.isFinite(Number(mfe));

  if (isOpen) {
    return (
      <section
        className={`flex min-h-0 min-w-0 w-full [container-type:inline-size] self-stretch flex-col overflow-hidden rounded-xl border border-[#242428] bg-[#1a1a1c] text-[#f4f4f5] shadow-md ${className}`}
        aria-label={title}
      >
        <header className="flex shrink-0 items-center gap-1.5 border-b border-[#242428] bg-[#1a1a1c] px-6 py-4">
          <h2 className="m-0 text-[15px] font-semibold tracking-normal">{title}</h2>
          <TooltipSimple text="Trade movement before exit.">
            <span className="inline-flex rounded-sm text-[#71717a] outline-none transition-colors hover:text-[#a1a1aa] focus-visible:ring-2 focus-visible:ring-sky-500/70" aria-label="Trade movement before exit." tabIndex={0}>
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
          </TooltipSimple>
        </header>
        <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center" aria-label="Position open">
          <p className="mt-3 max-w-[320px] text-sm leading-relaxed text-zinc-400">
            Position is open. Excursion metrics (Captured Profit, Efficiency, Exit Quality) become available after close.
          </p>
        </div>
      </section>
    );
  }

  if (availability === 'loading') {
    return (
      <section
        className={`flex min-h-0 min-w-0 w-full [container-type:inline-size] self-stretch flex-col overflow-hidden rounded-xl border border-[#242428] bg-[#1a1a1c] text-[#f4f4f5] shadow-md ${className}`}
        aria-label={title}
        aria-busy="true"
      >
        <header className="flex shrink-0 items-center gap-1.5 border-b border-[#242428] bg-[#1a1a1c] px-6 py-4">
          <h2 className="m-0 text-[15px] font-semibold tracking-normal">{title}</h2>
          <TooltipSimple text="Trade movement before exit.">
            <span className="inline-flex rounded-sm text-[#71717a] outline-none transition-colors hover:text-[#a1a1aa] focus-visible:ring-2 focus-visible:ring-sky-500/70" aria-label="Trade movement before exit." tabIndex={0}>
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
          </TooltipSimple>
        </header>
        <div className="flex flex-1 flex-col justify-center items-center px-6 py-8 text-center" role="status" aria-live="polite">
          <div className="flex items-center gap-3 rounded-2xl border border-zinc-700/80 bg-[#161617] px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
            <Loader2 className="h-5 w-5 animate-spin text-zinc-400" aria-hidden="true" />
            <span className="text-sm font-medium text-zinc-300">Calculating excursion…</span>
          </div>
          <p className="mt-4 max-w-[360px] text-sm leading-relaxed text-zinc-500">
            Fetching excursion metrics for the selected trade.
          </p>
        </div>
      </section>
    );
  }

  if (!hasCompleteExcursion) {
    return (
      <section
        className={`flex min-h-0 min-w-0 w-full [container-type:inline-size] self-stretch flex-col overflow-hidden rounded-xl border border-[#242428] bg-[#1a1a1c] text-[#f4f4f5] shadow-md ${className}`}
        aria-label={title}
      >
        <header className="flex shrink-0 items-center gap-1.5 border-b border-[#242428] bg-[#1a1a1c] px-6 py-4">
          <h2 className="m-0 text-[15px] font-semibold tracking-normal">{title}</h2>
          <TooltipSimple text="Trade movement before exit.">
            <span className="inline-flex rounded-sm text-[#71717a] outline-none transition-colors hover:text-[#a1a1aa] focus-visible:ring-2 focus-visible:ring-sky-500/70" aria-label="Trade movement before exit." tabIndex={0}>
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
          </TooltipSimple>
        </header>
        <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center" aria-label="Trade excursion unavailable">
          <div className="text-[clamp(1.75rem,10cqi,3.2rem)] font-bold leading-none text-zinc-400">Unavailable</div>
          <p className="mt-3 max-w-[320px] text-sm leading-relaxed text-zinc-400">
            {unavailableReason || 'A complete excursion series is unavailable for this trade.'}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`flex min-h-0 min-w-0 w-full [container-type:inline-size] self-stretch flex-col overflow-hidden rounded-xl border border-[#242428] bg-[#1a1a1c] text-[#f4f4f5] shadow-md transition-opacity duration-150 ${className}`}
      aria-label={title}
      aria-busy={isUpdating ? 'true' : undefined}
    >
      <header className="flex shrink-0 items-center justify-between gap-1.5 border-b border-[#242428] bg-[#1a1a1c] px-6 py-4">
        <div className="flex items-center gap-1.5">
          <h2 className="m-0 text-[15px] font-semibold tracking-normal">
            {title}
          </h2>
          <TooltipSimple text="Trade movement before exit.">
            <span
              className="inline-flex rounded-sm text-[#71717a] outline-none transition-colors hover:text-[#a1a1aa] focus-visible:ring-2 focus-visible:ring-sky-500/70"
              aria-label="Trade movement before exit."
              tabIndex={0}
            >
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
          </TooltipSimple>
        </div>
        {isUpdating ? (
          <span className="inline-flex items-center gap-2 rounded-full border border-zinc-700/90 bg-[#161617] px-3 py-1 text-xs font-medium text-zinc-300">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-sky-400" aria-hidden="true" />
            Updating…
          </span>
        ) : null}
      </header>

      <div className="-mt-[10px] flex min-h-0 flex-1 flex-col px-5 sm:px-6">
        <div className="relative mx-auto aspect-[2/1.06] w-full max-w-[430px] -translate-y-[12px]">
          <svg
            className="absolute left-1/2 top-0 mt-2.5 w-[92%] -translate-x-1/2 overflow-visible"
            viewBox="0 0 300 165"
            role="img"
            aria-label={gaugeAriaLabel}
          >
            <defs>
              <linearGradient id={`${id}-first`} gradientUnits="userSpaceOnUse" x1={firstStartPoint.x} y1={firstStartPoint.y} x2={firstEndPoint.x} y2={firstEndPoint.y}>
                <stop offset="0" stopColor={LOSS_COLOR} />
                <stop offset="1" stopColor={LOSS_COLOR} />
              </linearGradient>
              <linearGradient id={`${id}-second`} gradientUnits="userSpaceOnUse" x1={secondStartPoint.x} y1={secondStartPoint.y} x2={secondEndPoint.x} y2={secondEndPoint.y}>
                <stop offset="0" stopColor={PROFIT_COLOR} />
                <stop offset="1" stopColor={PROFIT_COLOR} />
              </linearGradient>
              <linearGradient id={`${id}-third`} gradientUnits="userSpaceOnUse" x1={thirdStartPoint.x} y1={thirdStartPoint.y} x2={thirdEndPoint.x} y2={thirdEndPoint.y}>
                <stop offset="0" stopColor={LEFT_ON_TABLE_ACCENT} />
                <stop offset="1" stopColor={LEFT_ON_TABLE_ACCENT} />
              </linearGradient>
              <filter id={`${id}-glow`} x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="1" result="near" />
                <feGaussianBlur stdDeviation="2.5" result="far" />
                <feMerge>
                  <feMergeNode in="far" />
                  <feMergeNode in="near" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <path d={INSET_PATH} fill="#151517" />
            <path d={ARC_PATH} pathLength={100} fill="none" stroke="#27272a" strokeWidth={9} strokeLinecap="butt" />
            <g filter={`url(#${id}-glow)`}>
              <path d={ARC_PATH} pathLength={100} fill="none" stroke={`url(#${id}-first)`} strokeWidth={9} strokeLinecap="butt" strokeDasharray={`${firstPct} 100`} />
              <path d={ARC_PATH} pathLength={100} fill="none" stroke={`url(#${id}-second)`} strokeWidth={9} strokeLinecap="butt" strokeDasharray={`${secondPct} 100`} strokeDashoffset={-firstEnd} />
              <path d={ARC_PATH} pathLength={100} fill="none" stroke={`url(#${id}-third)`} strokeWidth={9} strokeLinecap="butt" strokeDasharray={`${thirdPct} 100`} strokeDashoffset={-secondEnd} />
            </g>
            {data.efficiency != null ? <circle cx={marker.x} cy={marker.y} r={5} fill="#fff" /> : null}
          </svg>

          <div className="absolute inset-x-0 bottom-[11%] text-center">
            <div
              className="mb-2 bg-clip-text text-[clamp(2rem,11cqi,3.45rem)] font-bold leading-[0.9] tracking-[-0.055em] text-transparent"
              style={{ backgroundImage: mainMetricGradient }}
            >
              {data.efficiency == null ? '—' : `${Math.round(data.efficiency)}%`}
            </div>
            <p className="m-0 px-4 text-[10px] font-semibold tracking-[0.28em] text-[#aeb3c1] sm:text-[11px]">
              {gaugeLabel}
            </p>
          </div>
        </div>

        <div className="trade-excursion-summary grid shrink-0 grid-cols-1 gap-3 border-t border-[#242428] pb-5 pt-5">
          {isLoss ? (
            <>
              <MetricCard label="Max Drawdown" value={formatSigned(data.maxDrawdown, '-')} accent={LOSS_COLOR} valueColor={LOSS_COLOR} />
              <MetricCard label="Exit Recovery" value={formatSigned(data.recoveredBeforeExit, '+')} accent={PROFIT_COLOR} valueColor={PROFIT_COLOR} />
              <MetricCard label="Missed Upside" value={formatSigned(data.missedUpside, '+')} accent={LEFT_ON_TABLE_ACCENT} valueColor="#f4f4f5" />
            </>
          ) : (
            <>
              <MetricCard label="Heat Survived" value={formatSigned(data.heatSurvived, '-')} accent={LOSS_COLOR} valueColor={LOSS_COLOR} />
              <MetricCard label="Captured Profit" value={formatSigned(data.capturedProfit, '+')} accent={PROFIT_COLOR} valueColor={PROFIT_COLOR} />
              <MetricCard label="Left on Table" value={formatSigned(data.leftOnTable, '+')} accent={LEFT_ON_TABLE_ACCENT} valueColor="#f4f4f5" />
            </>
          )}
        </div>
      </div>
    </section>
  );
}
